import dayjs from "dayjs";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import de from "date-fns/locale/de";
import { type ReactNode } from "react";
import { type DateFilterMode } from "@/components/dateTime/genericDateFilterUtils";
import styles from "@/components/dateTime/genericDateRange.module.css";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("de", de);

export type CalendarPanelView = "days" | "months" | "years";
export type GenericDateRangeSelectionMode = "range" | "single";

export const YEARS_PER_PAGE = 12;

interface PanelHeaderProps {
  onPrevious: () => void;
  onNext: () => void;
  previousDisabled?: boolean;
  nextDisabled?: boolean;
  children: ReactNode;
}

const PanelHeader = ({
  onPrevious,
  onNext,
  previousDisabled = false,
  nextDisabled = false,
  children,
}: PanelHeaderProps) => (
  <div className={styles.panelHeader}>
    <button
      type="button"
      className={styles.navButton}
      onClick={onPrevious}
      disabled={previousDisabled}
      aria-label="Previous"
    >
      <ChevronLeftIcon fontSize="small" />
    </button>
    <div className={styles.panelHeaderCenter}>{children}</div>
    <button
      type="button"
      className={styles.navButton}
      onClick={onNext}
      disabled={nextDisabled}
      aria-label="Next"
    >
      <ChevronRightIcon fontSize="small" />
    </button>
  </div>
);

interface GenericCalendarPanelProps {
  panelView: CalendarPanelView;
  setPanelView: (view: CalendarPanelView) => void;
  viewDate: Date;
  setViewDate: (date: Date) => void;
  yearsPageStart: number;
  setYearsPageStart: (year: number) => void;
  locale: string | undefined;
  filterMode?: DateFilterMode;
  selectionMode?: GenericDateRangeSelectionMode;
  selected?: Date | null;
  startDate?: Date | null;
  endDate?: Date | null;
  onPickChange?: (update: [Date | null, Date | null]) => void;
  onOpenEndedChange?: (date: Date) => void;
  onSingleDateChange?: (date: Date) => void;
}

const formatMonthGridLabel = (month: number, dayjsLocale: string): string => {
  const monthNumber = String(month + 1).padStart(2, "0");
  const monthName = dayjs().locale(dayjsLocale).month(month).format("MMM");
  return `${monthNumber} ${monthName}`;
};

const CALENDAR_START_DAY = 1;

export default function GenericCalendarPanel({
  panelView,
  setPanelView,
  viewDate,
  setViewDate,
  yearsPageStart,
  setYearsPageStart,
  locale,
  filterMode = "pick",
  selectionMode = "single",
  selected = null,
  startDate = null,
  endDate = null,
  onPickChange,
  onOpenEndedChange,
  onSingleDateChange,
}: GenericCalendarPanelProps) {
  const dayjsLocale = locale === "de" ? "de" : "en";
  const viewDayjs = dayjs(viewDate).locale(dayjsLocale);

  if (panelView === "years") {
    const pageStart = yearsPageStart;
    const selectedYear = viewDate.getFullYear();
    const years = Array.from(
      { length: YEARS_PER_PAGE },
      (_, index) => pageStart + index,
    );

    return (
      <div className={styles.pickerPanel}>
        <PanelHeader
          onPrevious={() => setYearsPageStart(pageStart - YEARS_PER_PAGE)}
          onNext={() => setYearsPageStart(pageStart + YEARS_PER_PAGE)}
        >
          <span className={styles.panelTitle}>
            {pageStart} – {pageStart + YEARS_PER_PAGE - 1}
          </span>
        </PanelHeader>
        <div className={styles.yearGrid}>
          {years.map((year) => (
            <button
              key={year}
              type="button"
              className={`${styles.gridCell} ${
                year === selectedYear ? styles.gridCellActive : ""
              }`}
              onClick={() => {
                setViewDate(
                  dayjs(viewDate).year(year).month(0).date(1).toDate(),
                );
                setPanelView("months");
              }}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (panelView === "months") {
    const year = viewDate.getFullYear();

    return (
      <div className={styles.pickerPanel}>
        <PanelHeader
          onPrevious={() =>
            setViewDate(dayjs(viewDate).subtract(1, "year").toDate())
          }
          onNext={() => setViewDate(dayjs(viewDate).add(1, "year").toDate())}
        >
          <button
            type="button"
            className={styles.panelTitleButton}
            onClick={() => {
              setYearsPageStart(
                Math.floor(viewDate.getFullYear() / YEARS_PER_PAGE) *
                  YEARS_PER_PAGE,
              );
              setPanelView("years");
            }}
          >
            {year}
          </button>
        </PanelHeader>
        <div className={styles.monthGrid}>
          {Array.from({ length: 12 }, (_, month) => month).map((month) => (
            <button
              key={month}
              type="button"
              className={`${styles.gridCell} ${
                month === viewDate.getMonth() ? styles.gridCellActive : ""
              }`}
              onClick={() => {
                setViewDate(dayjs(viewDate).month(month).date(1).toDate());
                setPanelView("days");
              }}
            >
              {formatMonthGridLabel(month, dayjsLocale)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const renderCustomHeader = () => (
    <PanelHeader
      onPrevious={() =>
        setViewDate(dayjs(viewDate).subtract(1, "month").toDate())
      }
      onNext={() => setViewDate(dayjs(viewDate).add(1, "month").toDate())}
    >
      <button
        type="button"
        className={styles.panelTitleButton}
        onClick={() => setPanelView("months")}
      >
        {viewDayjs.format("MMMM")}
      </button>
      <button
        type="button"
        className={styles.panelTitleButton}
        onClick={() => {
          setYearsPageStart(
            Math.floor(viewDate.getFullYear() / YEARS_PER_PAGE) *
              YEARS_PER_PAGE,
          );
          setPanelView("years");
        }}
      >
        {viewDayjs.format("YYYY")}
      </button>
    </PanelHeader>
  );

  const isRangePickMode =
    selectionMode === "range" && filterMode === "pick";
  const calendarClassName = isRangePickMode
    ? `${styles.calendar} ${styles.dateRangeCalendar}`
    : styles.calendar;

  return (
    <div className={styles.pickerPanel}>
      <DatePicker
        inline
        locale={locale}
        calendarStartDay={CALENDAR_START_DAY}
        disabledKeyboardNavigation
        openToDate={viewDate}
        renderCustomHeader={renderCustomHeader}
        key={`days-${filterMode}-${viewDate.getFullYear()}-${viewDate.getMonth()}`}
        calendarClassName={calendarClassName}
        selectsRange={isRangePickMode}
        peekNextMonth={isRangePickMode}
        selected={isRangePickMode ? undefined : selected}
        startDate={isRangePickMode ? startDate : undefined}
        endDate={isRangePickMode ? endDate : undefined}
        onChange={(update) => {
          if (isRangePickMode) {
            onPickChange?.(
              (update ?? [null, null]) as [Date | null, Date | null],
            );
            return;
          }

          const date = update as Date | null;
          if (date) {
            if (selectionMode === "single") {
              onSingleDateChange?.(date);
            } else {
              onOpenEndedChange?.(date);
            }
          }
        }}
      />
    </div>
  );
}
